import React, { useEffect, useState } from 'react';
import { cashService } from '../services/api';

const KIND_LABEL = { session: 'Horas (sessões)', package: 'Pacotes', product: 'Lanchonete', topup: 'Recargas', manual: 'Lançamentos manuais' };
const METHOD_LABEL = { cash: 'Dinheiro', pix: 'PIX', card_debit: 'Cartão débito', card_credit: 'Cartão crédito', courtesy: 'Cortesia / Isento', balance: 'Saldo', mercadopago: 'Mercado Pago' };
const PAGE_SIZE = 20;

// Navegação das listas longas. Some quando cabe tudo numa página só.
function Paginator({ page, pages, onChange }) {
  if (pages <= 1) return null;
  return (
    <div style={styles.pager}>
      <button style={styles.pagerBtn} disabled={page <= 1} onClick={() => onChange(page - 1)}>‹</button>
      <span style={styles.pagerInfo}>{page} de {pages}</span>
      <button style={styles.pagerBtn} disabled={page >= pages} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}

const pageSlice = (items, page) => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
const pageCount = items => Math.max(1, Math.ceil(items.length / PAGE_SIZE));

// `range` vem do cabeçalho do Financeiro (hoje, ontem, 7 dias, mês, período).
// Sem ele o componente se vira sozinho e mostra o dia corrente.
export default function Cashier({ range }) {
  const [current, setCurrent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [modal, setModal] = useState(null);   // 'open' | 'close' | 'edit_payment'
  const [editingPayment, setEditingPayment] = useState(null);
  const [newMethod, setNewMethod] = useState('pix');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [movements, setMovements] = useState([]);
  const [history, setHistory] = useState([]);
  const [pendingPix, setPendingPix] = useState({ data: [], aguardando: 0, valor_aguardando: 0 });
  const [historyMeta, setHistoryMeta] = useState({ page: 1, pages: 1 });
  const [pixPage, setPixPage] = useState(1);
  const [movPage, setMovPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const periodo = range?.from && range?.to ? { from: range.from, to: range.to } : {};
    const [c, s, h] = await Promise.all([cashService.current(), cashService.summary(periodo), cashService.history({page:1})]);
    setCurrent(c.data);
    setSummary(s.data);
    setHistory(h.data?.data || []);
    setHistoryMeta({ page: h.data?.pagination?.page || 1, pages: h.data?.pagination?.pages || 1 });
    const m = await cashService.movements(c.data?.id ? { register_id: c.data.id } : {});
    setMovements(m.data || []);
    try { setPendingPix((await cashService.pendingPix()).data); } catch (_) {}
  };
  // Recarrega quando o cabeçalho troca o período (hoje, ontem, 7 dias, mês…)
  useEffect(() => { load(); }, [range?.from, range?.to]);

  // O histórico é paginado no servidor (20 por página); as outras duas listas
  // já vêm inteiras e são fatiadas aqui mesmo.
  const loadHistory = async (page) => {
    const h = await cashService.history({ page });
    setHistory(h.data?.data || []);
    setHistoryMeta({ page: h.data?.pagination?.page || page, pages: h.data?.pagination?.pages || 1 });
  };

  const money = (v) => `R$ ${Number(v || 0).toFixed(2)}`;

  // Rótulo do período em uso, para os títulos não dizerem "hoje" olhando o mês.
  const br = d => String(d).split('-').reverse().join('/');
  const periodoLabel = !range?.from ? 'hoje'
    : range.from === range.to ? br(range.from)
    : `${br(range.from)} a ${br(range.to)}`;

  const handleOpen = async () => {
    setLoading(true);
    try { await cashService.open({ opening_amount: parseFloat(amount) || 0 }); setModal(null); setAmount(''); load(); }
    catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  };
  const handleClose = async () => {
    setLoading(true);
    try { const r=await cashService.close({ closing_amount: parseFloat(amount) || 0,notes:description }); alert(`Caixa fechado. Esperado: ${money(r.data.expected_amount)} · Diferença: ${money(r.data.difference_amount)}`); setModal(null); setAmount(''); setDescription(''); load(); }
    catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  };
  const handleMovement=async()=>{setLoading(true);try{await cashService.addMovement({movement_type:modal,amount:Number(amount),description});setModal(null);setAmount('');setDescription('');load()}catch(err){alert('Erro: '+(err.response?.data?.error||err.message))}finally{setLoading(false)}};
  const movementLabel={reinforcement:'Reforço de caixa',withdrawal:'Sangria',expense:'Despesa em dinheiro',income:'Entrada avulsa'};

  const handleUpdatePaymentMethod = async () => {
    if (!editingPayment) return;
    setLoading(true);
    try {
      await cashService.updatePaymentMethod(editingPayment.id, newMethod);
      alert('Forma de pagamento atualizada com sucesso!');
      setModal(null);
      setEditingPayment(null);
      load();
    } catch (err) {
      alert('Erro ao alterar: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Caixa do dia</h1>
          <p style={styles.sub}>Dinheiro que entrou de verdade e controle de abertura/fechamento</p>
        </div>
        {current
          ? <button style={styles.btnDanger} onClick={() => { setAmount(''); setDescription(''); setModal('close'); }}>Fechar caixa</button>
          : <button style={styles.btnPrimary} onClick={() => { setAmount(''); setDescription(''); setModal('open'); }}>Abrir caixa</button>}
      </div>

      {/* STATUS DO CAIXA */}
      <div style={{ ...styles.statusCard, borderLeft: `3px solid ${current ? '#10b981' : '#6b7280'}` }}>
        {current ? (
          <>
            <span style={styles.statusDot}>🟢 Caixa aberto</span>
            <span style={styles.statusInfo}>por {current.operator_name} · abertura {money(current.opening_amount)}</span>
          </>
        ) : (
          <span style={styles.statusInfo}>⚪ Nenhum caixa aberto</span>
        )}
      </div>

      {current && <>
        <div style={styles.cashGrid}>
          <CashValue label="Abertura" value={current.opening_amount}/><CashValue label="Vendas em dinheiro" value={current.cash_sales}/><CashValue label="Reforços/entradas" value={current.additions}/><CashValue label="Sangrias/despesas" value={current.deductions} negative/><CashValue label="ESPERADO NA GAVETA" value={current.expected_now} highlight/>
        </div>
        <div style={styles.cashActions}>
          <button style={styles.btnPrimary} onClick={()=>{setModal('reinforcement');setAmount('');setDescription('')}}>+ Reforço</button>
          <button style={styles.btnSecondary} onClick={()=>{setModal('income');setAmount('');setDescription('')}}>+ Entrada avulsa</button>
          <button style={styles.btnWarning} onClick={()=>{setModal('withdrawal');setAmount('');setDescription('')}}>− Sangria</button>
          <button style={styles.btnDanger} onClick={()=>{setModal('expense');setAmount('');setDescription('')}}>− Despesa</button>
        </div>

        {/* LISTA DETALHADA DE VENDAS EM DINHEIRO NESTE CAIXA */}
        <div style={{...styles.panel, marginTop: 16, marginBottom: 20}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12}}>
            <div style={styles.panelTitle}>📋 Lançamentos em Dinheiro neste Caixa ({current.cash_sales_list?.length || 0})</div>
            <small style={{color:'#8b8b9b'}}>Verifique se algum foi lançado em dinheiro por engano</small>
          </div>
          {(current.cash_sales_list || []).map(p => (
            <div key={p.id} style={styles.lineItem}>
              <div>
                <b style={{color: '#fff'}}>{p.client_nickname || p.client_name || 'Cliente / Consumidor Balcão'}</b>
                <small style={styles.moveInfo}>
                  {new Date(p.paid_at).toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit'})} · {KIND_LABEL[p.kind] || p.kind} {p.notes ? `(${p.notes})` : ''} · {p.operator_name || 'Sistema'}
                </small>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                <b style={{...styles.lineValue, color: '#bef264'}}>{money(p.amount)}</b>
                <button
                  style={{padding: '5px 10px', background: '#262636', border: '1px solid #45455a', color: '#a3e635', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700}}
                  onClick={() => { setEditingPayment(p); setNewMethod(p.payment_method || 'cash'); setModal('edit_payment'); }}
                >
                  ✎ Alterar
                </button>
              </div>
            </div>
          ))}
          {!(current.cash_sales_list || []).length && (
            <div style={styles.emptySmall}>Nenhum lançamento registrado como dinheiro neste caixa.</div>
          )}
        </div>
      </>}

      {/* TOTAL DO PERÍODO SELECIONADO NO CABEÇALHO */}
      <div style={styles.bigTotal}>
        <div style={styles.bigLabel}>VENDAS FINANCEIRAS · {periodoLabel.toUpperCase()}</div>
        <div style={styles.bigValue}>{money(summary?.total)}</div>
        <div style={styles.bigSub}>{summary?.count || 0} transações</div>
      </div>

      {/* CARDS POR ORIGEM */}
      <div style={styles.cardsRow}>
        {(summary?.by_kind || []).map(k => (
          <div key={k.kind} style={styles.miniCard}>
            <div style={styles.miniLabel}>{KIND_LABEL[k.kind] || k.kind}</div>
            <div style={styles.miniValue}>{money(k.total)}</div>
            <div style={styles.miniSub}>{k.qtd} venda(s)</div>
          </div>
        ))}
        {!(summary?.by_kind || []).length && <div style={styles.empty}>Sem vendas em {periodoLabel}.</div>}
      </div>

      <div style={styles.twoCols}>
        {/* POR MÉTODO */}
        <div style={styles.panel}>
          <div style={styles.panelTitle}>Por forma de pagamento</div>
          {(summary?.by_method || []).map(m => (
            <div key={m.payment_method} style={styles.lineItem}>
              <span>{METHOD_LABEL[m.payment_method] || m.payment_method}</span>
              <span style={styles.lineValue}>{money(m.total)}</span>
            </div>
          ))}
          {!(summary?.by_method || []).length && <div style={styles.emptySmall}>—</div>}
        </div>

        {/* MARGEM LANCHONETE */}
        <div style={styles.panel}>
          <div style={styles.panelTitle}>Lanchonete · {periodoLabel}</div>
          <div style={styles.lineItem}><span>Receita</span><span style={styles.lineValue}>{money(summary?.products?.revenue)}</span></div>
          <div style={styles.lineItem}><span>Lucro (margem)</span><span style={{ ...styles.lineValue, color: '#34d399' }}>{money(summary?.products?.margin)}</span></div>
          <div style={styles.lineItem}><span>Itens vendidos</span><span style={styles.lineValue}>{summary?.products?.units || 0}</span></div>
        </div>
      </div>

      {/* Listas longas lado a lado: a grade acomoda 1, 2 ou 3 colunas conforme a largura */}
      <div style={styles.listGrid}>
      {pendingPix.data.length > 0 && (
        <div style={styles.panel}>
          <div style={styles.panelTitle}>
            Recargas PIX
            {pendingPix.aguardando > 0 && <span style={{color:'#fbbf24', fontWeight:400, fontSize:13, marginLeft:8}}>
              {pendingPix.aguardando} em aberto · {money(pendingPix.valor_aguardando)}
            </span>}
          </div>
          <div style={{color:'#8b8b9b', fontSize:12, marginBottom:10}}>
            Não entram no faturamento. Só viram receita quando o Mercado Pago confirmar.
          </div>
          {pageSlice(pendingPix.data, pixPage).map(p => {
            const rotulo = { pending:'⏳ Aguardando', cancelled:'❌ Não pago', rejected:'❌ Recusado', expired:'❌ Expirado' }[p.situacao] || p.situacao;
            const cor = p.situacao === 'pending' ? '#fbbf24' : '#6b7280';
            return (
              <div key={p.id} style={styles.lineItem}>
                <span>
                  <b>{p.client_nickname || p.client_name || 'Cliente'}</b>
                  <small style={styles.moveInfo}>
                    {p.gerado_em ? new Date(p.gerado_em).toLocaleString('pt-BR', {day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'}) : '—'}
                    {' · '}{KIND_LABEL[p.kind] || p.kind}
                  </small>
                </span>
                <span style={{textAlign:'right'}}>
                  <b style={styles.lineValue}>{money(p.amount)}</b>
                  <small style={{...styles.moveInfo, color:cor}}>{rotulo}</small>
                </span>
              </div>
            );
          })}
          <Paginator page={pixPage} pages={pageCount(pendingPix.data)} onChange={setPixPage} />
        </div>
      )}

      <div style={styles.panel}><div style={styles.panelTitle}>Movimentações da gaveta<span style={styles.panelHint}> · caixa aberto</span></div>{pageSlice(movements, movPage).map(m=><div key={m.id} style={styles.lineItem}><span><b>{movementLabel[m.movement_type]}</b><small style={styles.moveInfo}>{m.description} · {m.operator_name}</small></span><span style={{...styles.lineValue,color:['withdrawal','expense'].includes(m.movement_type)?'#fb7185':'#4ade80'}}>{['withdrawal','expense'].includes(m.movement_type)?'− ':'+ '}{money(m.amount)}</span></div>)}{!movements.length&&<div style={styles.emptySmall}>Nenhuma movimentação manual neste caixa.</div>}<Paginator page={movPage} pages={pageCount(movements)} onChange={setMovPage} /></div>
      <div style={styles.panel}><div style={styles.panelTitle}>Histórico de caixas</div>{history.map(h=><div key={h.id} style={styles.lineItem}><span><b>{h.status==='open'?'Caixa aberto':'Caixa fechado'}</b><small style={styles.moveInfo}>{new Date(h.opened_at).toLocaleString('pt-BR')} · {h.operator_name}</small></span><span style={{textAlign:'right'}}><b style={styles.lineValue}>{h.status==='open'?'Em andamento':money(h.closing_amount)}</b>{h.status==='closed'&&<small style={{...styles.moveInfo,color:h.difference_amount==null?'#8b8b9b':Number(h.difference_amount)===0?'#4ade80':'#fb7185'}}>{h.difference_amount==null?'Fechamento antigo · não conferido':`Diferença: ${money(h.difference_amount)}`}</small>}</span></div>)}<Paginator page={historyMeta.page} pages={historyMeta.pages} onChange={loadHistory} /></div>
      </div>

      {/* MODAL ABRIR/FECHAR/EDITAR FORMA */}
      {modal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            {modal === 'edit_payment' ? (
              <>
                <h2 style={styles.modalTitle}>Alterar Forma de Pagamento</h2>
                <p style={{color: '#9ca3af', fontSize: 13, marginBottom: 16}}>
                  Lançamento de <b>{money(editingPayment?.amount)}</b> em {editingPayment?.client_nickname || editingPayment?.client_name || 'Balcão'}.
                </p>
                <p style={{color:'#d9f99d',fontSize:13,margin:'-8px 0 16px'}}>
                  Forma atual: <b>{METHOD_LABEL[editingPayment?.payment_method] || editingPayment?.payment_method || '—'}</b>
                </p>
                <label style={styles.label}>Selecione a forma real de pagamento</label>
                <select
                  style={styles.input}
                  value={newMethod}
                  onChange={e => setNewMethod(e.target.value)}
                >
                  <option value="pix">PIX</option>
                  <option value="card_debit">Cartão Débito</option>
                  <option value="card_credit">Cartão Crédito</option>
                  <option value="courtesy">Cortesia / Isento</option>
                  <option value="cash">Dinheiro (manter)</option>
                </select>
                <p style={{color: '#777789', fontSize: 11, marginTop: 10}}>
                  Ao alterar de Dinheiro para outra forma, este valor será removido do cálculo do caixa esperado na gaveta física.
                </p>
                <div style={styles.modalActions}>
                  <button style={styles.btnGhost} onClick={() => setModal(null)}>Cancelar</button>
                  <button style={styles.btnPrimary} onClick={handleUpdatePaymentMethod} disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar Alteração'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 style={styles.modalTitle}>{modal === 'open' ? 'Abrir caixa' : modal === 'close' ? 'Fechar caixa' : movementLabel[modal]}</h2>
                {modal==='close'&&<div style={styles.expected}>Valor esperado: <b>{money(current?.expected_now)}</b></div>}
                <label style={styles.label}>{modal === 'open' ? 'Valor de abertura (troco inicial)' : modal==='close' ? 'Valor contado na gaveta' : 'Valor'}</label>
                <input type="number" step="0.01" style={styles.input} value={amount} onChange={e => setAmount(e.target.value)} autoFocus />
                {modal!=='open'&&<><label style={{...styles.label,marginTop:12}}>Descrição {modal==='close'?'(opcional)':''}</label><input style={styles.input} value={description} onChange={e=>setDescription(e.target.value)} placeholder={modal==='close'?'Observações do fechamento':'Motivo da movimentação'}/></>}
                <div style={styles.modalActions}>
                  <button style={styles.btnGhost} onClick={() => setModal(null)}>Cancelar</button>
                  <button style={modal === 'open'||['reinforcement','income'].includes(modal) ? styles.btnPrimary : styles.btnDanger}
                    onClick={modal === 'open' ? handleOpen : modal==='close'?handleClose:handleMovement} disabled={loading||(!['open','close'].includes(modal)&&(!amount||!description))}>
                    {loading ? '...' : (modal === 'open' ? 'Abrir' : modal==='close'?'Fechar e conferir':'Registrar')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 },
  title: { color: '#fff', fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { color: '#6b7280', fontSize: 14, margin: '4px 0 0' },
  btnPrimary: { padding: '10px 18px', background: 'var(--accent)', color: '#0a0a0f', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnDanger: { padding: '10px 18px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnSecondary:{padding:'10px 18px',background:'#0ea5e9',color:'#fff',border:0,borderRadius:8,cursor:'pointer',fontWeight:700},btnWarning:{padding:'10px 18px',background:'#f59e0b',color:'#111',border:0,borderRadius:8,cursor:'pointer',fontWeight:700},
  btnGhost: { padding: '10px 18px', background: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: 8, cursor: 'pointer', fontSize: 14 },
  statusCard: { background: '#14141f', border: '1px solid #262636', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 14, alignItems: 'center' },
  statusDot: { color: '#e5e7eb', fontSize: 14, fontWeight: 600 },
  statusInfo: { color: '#9ca3af', fontSize: 13 },
  bigTotal: { background: 'linear-gradient(135deg, #14210a, #14141f)', border: '1px solid #3f6212', borderRadius: 16, padding: 24, marginBottom: 20 },
  bigLabel: { color: '#bef264', fontSize: 12, fontWeight: 700, letterSpacing: 2 },
  bigValue: { color: '#fff', fontSize: 42, fontWeight: 800, margin: '6px 0' },
  bigSub: { color: '#6b7280', fontSize: 13 },
  cashGrid:{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(145px,1fr))',gap:10,marginBottom:12},cashValue:{background:'#14141f',border:'1px solid #2e2e40',borderRadius:11,padding:14,display:'flex',flexDirection:'column',gap:5},cashValueHighlight:{borderColor:'#84cc16',background:'#18230d'},cashValueLabel:{color:'#8b8b9b',fontSize:11,fontWeight:800},cashValueAmount:{color:'#fff',fontSize:19,fontWeight:900},cashActions:{display:'flex',gap:8,flexWrap:'wrap',marginBottom:20},
  cardsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 20 },
  miniCard: { background: '#14141f', border: '1px solid #262636', borderRadius: 12, padding: 16 },
  miniLabel: { color: '#9ca3af', fontSize: 12, fontWeight: 600 },
  miniValue: { color: '#fff', fontSize: 22, fontWeight: 700, margin: '4px 0' },
  miniSub: { color: '#6b7280', fontSize: 12 },
  twoCols: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 },
  panel: { background: '#14141f', border: '1px solid #262636', borderRadius: 12, padding: 18 },
  // Quebra em 1, 2 ou 3 colunas conforme a largura disponível; cada coluna nunca
  // fica menor que 340px, para as linhas de valor não espremerem.
  listGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginTop: 16, alignItems: 'start' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 10, paddingTop: 10, borderTop: '1px solid #262636' },
  pagerBtn: { background: '#1c1c2b', border: '1px solid #2f2f45', color: '#d1d5db', borderRadius: 6, width: 28, height: 26, cursor: 'pointer', fontSize: 14, lineHeight: 1 },
  pagerInfo: { color: '#8b8b9b', fontSize: 12 },
  panelHint: { color: '#6b7280', fontSize: 12, fontWeight: 400 },
  panelTitle: { color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 12 },
  lineItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #262636', color: '#9ca3af', fontSize: 14 },
  lineValue: { color: '#e5e7eb', fontWeight: 600 },
  empty: { color: '#6b7280', padding: 20, gridColumn: '1 / -1' },
  emptySmall: { color: '#6b7280', fontSize: 13 },
  moveInfo:{display:'block',fontSize:11,color:'#777789',marginTop:3},expected:{background:'#202b12',border:'1px solid #4d7c0f',padding:12,borderRadius:8,color:'#d9f99d',marginBottom:14},
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#14141f', border: '1px solid #262636', borderRadius: 16, padding: 28, width: 400, maxWidth: '90vw' },
  modalTitle: { color: '#fff', fontSize: 20, fontWeight: 700, margin: '0 0 16px' },
  label: { display: 'block', color: '#9ca3af', fontSize: 13, fontWeight: 500, marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 8, color: '#fff', fontSize: 14, boxSizing: 'border-box' },
  modalActions: { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 },
};

function CashValue({label,value,negative,highlight}){return <div style={{...styles.cashValue,...(highlight?styles.cashValueHighlight:{})}}><span style={styles.cashValueLabel}>{label}</span><strong style={{...styles.cashValueAmount,color:negative?'#fb7185':highlight?'#bef264':'#fff'}}>{negative?'− ':''}{moneyStatic(value)}</strong></div>}
const moneyStatic=v=>`R$ ${Number(v||0).toFixed(2)}`;
